import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDatasetData } from '../../dataset'; // ⬅️ adjust path if needed

/**
 * useDataset
 *
 * Options:
 *   - datasetMgr?: manager with getItem(id) -> { Items: [dataset] }
 *   - initialDataset?: dataset object to pre-load
 *
 * Exposes:
 *   - Provider passthrough: engineReady, engineError, initEngine, statusByDatasetId, getEngineInfo, flushFiles
 *   - UI state: selectedDatasetId, setSelectedDatasetId, loadedDataset,
 *               intervalMs, setIntervalMs, queryText, setQueryText, lastQueryRows,
 *               output, resolvedTable
 *   - History: history[], clearHistory(), getHistoryText()
 *   - Actions (accept dataset OR datasetId OR undefined=selected):
 *       loadAndRegister(input?, { refreshOnAdd })
 *       registerDataset(dataset, { refreshOnAdd })
 *       onManualRefresh(input?)
 *       onSetInterval(input?)
 *       onRunQuery(input?, sql?)
 *       onListDatasets()
 *       onEngineDiagnostics()
 *       onReinitDb(opts?)
 *       onCopyDebugBundle()
 */
export function useDataset({ datasetMgr, initialDataset } = {}) {
  // ---- Provider API (from your Dataset provider) ----
  const {
    engineReady,
    engineError,
    initEngine,
    getEngineDiagnostics,
    addDataset,
    refreshDataset,
    setRefreshPlan,
    listDatasets,
    queryDataset,
    getTableName,
    statusByDatasetId,
    buildDebugBundle,
    reinitEngineDb,
    getEngineInfo,
    flushFiles,
    subscribeDataset,
  } = useDatasetData();

  // ---- Local UI state ----
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [loadedDataset, setLoadedDataset] = useState(initialDataset ?? null);
  const [intervalMs, setIntervalMs] = useState('');
  const [queryText, setQueryText] = useState('SELECT 1 AS ok');
  const [lastQueryRows, setLastQueryRows] = useState([]);

  // current output (unchanged contract)
  const [output, _setOutput] = useState({ text: '', isError: false });

  // ---- History (with datasetId) ----
  const [history, setHistory] = useState([]); // [{ ts, type:'info'|'error', text, datasetId, meta? }]

  // bump cap
  const HISTORY_MAX = 10000;

  // localStorage helpers (per dataset)
  const HISTORY_NS = 'dataset.history';
  const keyFor = useCallback(
    (dsid) => `${HISTORY_NS}.${dsid || '_global'}`,
    []
  );

  const loadHistoryFromStorage = useCallback((dsid) => {
    try {
      const raw = localStorage.getItem(keyFor(dsid));
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }, [keyFor]);

  const saveHistoryToStorage = useCallback((dsid, arr) => {
    try {
      localStorage.setItem(keyFor(dsid), JSON.stringify(arr));
    } catch {
      /* ignore write failures */
    }
  }, [keyFor]);

  // Load stored history whenever the selected dataset changes
  useEffect(() => {
    const dsid = selectedDatasetId || null;
    const stored = loadHistoryFromStorage(dsid);
    setHistory(stored);
  }, [selectedDatasetId, loadHistoryFromStorage]);

  const pushHistory = useCallback((type, text, datasetId, meta) => {
    const dsid = datasetId ?? selectedDatasetId ?? null;
    const entry = {
      ts: new Date().toISOString(),
      type,
      text: String(text ?? ''),
      datasetId: dsid,
      ...(meta ? { meta } : {}),
    };

    // read existing for this dataset, append, cap and persist
    const existing = loadHistoryFromStorage(dsid);
    const next = [...existing, entry];
    if (next.length > HISTORY_MAX) next.splice(0, next.length - HISTORY_MAX);
    saveHistoryToStorage(dsid, next);

    // reflect in-memory if this is the active dataset
    if (dsid === (selectedDatasetId || null)) {
      setHistory(next);
    }

    return entry;
  }, [selectedDatasetId, loadHistoryFromStorage, saveHistoryToStorage]);

  const logInfo  = useCallback(
    (text, meta, datasetId) => pushHistory('info',  text, datasetId ?? selectedDatasetId, meta),
    [pushHistory, selectedDatasetId]
  );
  const logError = useCallback(
    (text, meta, datasetId) => pushHistory('error', text, datasetId ?? selectedDatasetId, meta),
    [pushHistory, selectedDatasetId]
  );

  const clearHistory = useCallback(() => {
    const dsid = selectedDatasetId || null;
    try { localStorage.removeItem(keyFor(dsid)); } catch {}
    setHistory([]);
  }, [selectedDatasetId, keyFor]);

  const getHistoryText = useCallback(() =>
    history.slice().reverse().map(e => {
      const meta = e.meta ? ` ${JSON.stringify(e.meta)}` : '';
      const dsid = e.datasetId ? ` (ds:${e.datasetId})` : '';
      return `[${e.ts}] ${e.type.toUpperCase()}: ${e.text}${dsid}${meta}`;
    }).join('\n')
  , [history]);

  // keep existing output semantics; also log with datasetId
  // NOTE: datasetIdOverride lets callers stamp a specific dataset id
  const setOutputMessage = useCallback((text, isError = false, meta, datasetIdOverride) => {
    _setOutput({ text, isError });
    const dsid = datasetIdOverride ?? selectedDatasetId ?? null;
    (isError ? logError : logInfo)(text, meta, dsid);
    if (!isError) console.log('[dataset]', text, dsid ? `(ds:${dsid})` : '');
  }, [logInfo, logError, selectedDatasetId]);

  // Try to init engine on mount (safe if provider already auto-inits)
  useEffect(() => {
    if (!engineReady && typeof initEngine === 'function') {
      void initEngine();
    }
  }, [engineReady, initEngine]);

  // Resolved table name for the selected dataset
  const resolvedTable = useMemo(
    () => (selectedDatasetId ? getTableName(selectedDatasetId) : null),
    [selectedDatasetId, getTableName]
  );

  // ---- helpers ----
  const formatError = useCallback((e) => {
    const base = e?.message || String(e);
    const info = e?.info ? `\nDetails: ${JSON.stringify(e.info)}` : '';
    return `${base}${info}`;
  }, []);

  const copyToClipboard = useCallback(async (text) => {
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
    } catch { /* fall back below */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      return true;
    } catch { return false; }
  }, []);

  // --- helpers for display/consistency ---
  const normalizeRowCount = useCallback((rc) => {
    if (typeof rc === 'bigint') return rc.toString();
    if (typeof rc === 'number') {
      if (!Number.isFinite(rc)) return 0;
      const t = Math.trunc(rc);
      return t < 0 ? 0 : t;
    }
    return rc == null ? 0 : rc;
  }, []);

  // ---- dataset resolution ----
  async function resolveDataset(input) {
    if (input && typeof input === 'object') return input; // already a dataset
    const id = input ?? selectedDatasetId;
    if (!id) throw new Error('Pick a dataset first.');
    if (!datasetMgr?.getItem) throw new Error('datasetMgr.getItem not available to load by id.');
    const result = await datasetMgr.getItem(id);
    const ds = (result?.Items ?? [])[0];
    if (!ds?._id) throw new Error('Dataset not found or missing _id.');
    return ds;
  }

  // =======================
  // 🔴 Live updates (no loader)
  // =======================
  useEffect(() => {
    // Only subscribe once a dataset is selected and provider exposes subscription
    if (!selectedDatasetId || typeof subscribeDataset !== 'function') return;

    const unsubscribe = subscribeDataset(selectedDatasetId, (payload) => {
      // payload: { rows, rowCount, datasetId, at }
      const rows = Array.isArray(payload?.rows) ? payload.rows : [];
      setLastQueryRows(rows);
      // Optional: log lightweight info line (won’t trigger loader)
      try {
        const count = payload?.rowCount ?? rows.length ?? 0;
        logInfo(`Live update applied: ${count} rows.`, { at: payload?.at }, selectedDatasetId);
      } catch {}
    });

    return () => { try { unsubscribe?.(); } catch {} };
  }, [selectedDatasetId, subscribeDataset, logInfo]);

  // ---- ACTIONS ----

  // Load + register dataset (id | dataset | undefined uses selectedDatasetId)
  const loadAndRegister = useCallback(async (input, { refreshOnAdd = false } = {}) => {
    try {
      const startId = typeof input === 'object' ? input?._id : (typeof input === 'string' ? input : selectedDatasetId);
      setOutputMessage('Loading dataset…', false, undefined, startId);

      const ds = await resolveDataset(input);
      setLoadedDataset(ds);

      const label = ds.Label || ds.Name || ds._id;
      setOutputMessage(
        refreshOnAdd
          ? `Registering ${label} with engine and performing initial refresh…`
          : `Registering ${label} with engine…`,
        false, undefined, ds._id
      );

      await addDataset(ds, { refreshOnAdd: !!refreshOnAdd });

      setOutputMessage(
        refreshOnAdd
          ? `Dataset ${label} registered; initial refresh completed.`
          : `Dataset ${label} registered.`,
        false, undefined, ds._id
      );
      return ds;
    } catch (e) {
      const dsid = (typeof input === 'object' && input?._id) || (typeof input === 'string' ? input : selectedDatasetId);
      setOutputMessage(`Load/Register failed: ${formatError(e)}`, true, undefined, dsid);
      throw e;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addDataset, selectedDatasetId, setOutputMessage, formatError, datasetMgr]);

  // Register dataset directly (no fetch)
  const registerDataset = useCallback(async (ds, { refreshOnAdd = false  } = {}) => {
    if (!ds?._id) throw new Error('Dataset missing _id.');
    try {
      setLoadedDataset(ds);
      const label = ds.Label || ds.Name || ds._id;
      setOutputMessage(
        refreshOnAdd
          ? `Registering ${label} with engine and performing initial refresh…`
          : `Registering ${label} with engine…`,
        false, undefined, ds._id
      );
      await addDataset(ds, { refreshOnAdd: !!refreshOnAdd });
      setOutputMessage(
        refreshOnAdd
          ? `Dataset ${label} registered; initial refresh completed.`
          : `Dataset ${label} registered.`,
        false, undefined, ds._id
      );
      return ds;
    } catch (e) {
      setOutputMessage(`Register failed: ${formatError(e)}`, true, undefined, ds?._id);
      throw e;
    }
  }, [addDataset, setOutputMessage, formatError]);

  const onManualRefresh = useCallback(async (idOrDs) => {
    const ds = typeof idOrDs === 'object' ? idOrDs : null;
    const id = ds?._id ?? (typeof idOrDs === 'string' ? idOrDs : selectedDatasetId);
    if (!id) { setOutputMessage('Pick a dataset first.'); return; }
    try {
      setOutputMessage('Manual refresh started…', false, undefined, id);
      await refreshDataset(id);
      setOutputMessage('Manual refresh completed.', false, undefined, id);
    } catch (e) {
      setOutputMessage(`Manual refresh failed: ${formatError(e)}`, true, undefined, id);
    }
  }, [selectedDatasetId, refreshDataset, setOutputMessage, formatError]);

  const onSetInterval = useCallback(async (idOrDs) => {
    const ds = typeof idOrDs === 'object' ? idOrDs : null;
    const id = ds?._id ?? (typeof idOrDs === 'string' ? idOrDs : selectedDatasetId);
    if (!id) { setOutputMessage('Pick a dataset first.'); return; }
    try {
      const val = (intervalMs ?? '').trim?.() ?? '';
      const ms = val ? Number(val) : null;
      if (val && (Number.isNaN(ms) || ms < 0)) {
        setOutputMessage('Interval must be a non-negative number (ms) or blank to clear.', true, undefined, id);
        return;
      }
      await setRefreshPlan(id, ms);
      setOutputMessage(ms ? `Refresh plan set to ${ms} ms.` : 'Refresh plan cleared.', false, undefined, id);
    } catch (e) {
      setOutputMessage(`Set/Clear plan failed: ${formatError(e)}`, true, undefined, id);
    }
  }, [selectedDatasetId, intervalMs, setRefreshPlan, setOutputMessage, formatError]);

  const onRunQuery = useCallback(async (idOrDs, sqlMaybe) => {
    const sql = (sqlMaybe ?? queryText ?? '').trim();
    const ds = typeof idOrDs === 'object' ? idOrDs : null;
    const id = ds?._id ?? (typeof idOrDs === 'string' ? idOrDs : selectedDatasetId);
    if (!id) { setOutputMessage('Pick a dataset first.'); return; }
    if (!sql) { setOutputMessage('Enter a SQL query.', false, undefined, id); return; }
    try {
      setOutputMessage('Running query…', false, undefined, id);
      const rows = await queryDataset(id, sql);
      setLastQueryRows(rows);
      setOutputMessage(`Query returned ${rows.length} rows.`, false, { sqlPreview: sql.slice(0, 200) }, id);
      return rows;
    } catch (e) {
      setLastQueryRows([]);
      setOutputMessage(`Query failed: ${formatError(e)}`, true, { sqlPreview: sql.slice(0, 200) }, id);
      console.error(e);
      throw e;
    }
  }, [selectedDatasetId, queryText, queryDataset, setOutputMessage, formatError]);

  const onListDatasets = useCallback(async () => {
    try {
      if (listDatasets) {
        const list = await listDatasets();
        const cleaned = (Array.isArray(list) ? list : []).map(d => ({
          ...d,
          rowCount: normalizeRowCount(d?.rowCount),
          refreshPlanMs:
            typeof d?.refreshPlanMs === 'number' && Number.isFinite(d.refreshPlanMs)
              ? Math.max(0, Math.trunc(d.refreshPlanMs))
              : d?.refreshPlanMs ?? null,
        }));
        setOutputMessage(`Engine datasets:\n${JSON.stringify(cleaned, null, 2)}`);
        return cleaned;
      }
      setOutputMessage('Engine datasets not implemented yet.');
      return [];
    } catch (e) {
      setOutputMessage(`List datasets failed: ${formatError(e)}`, true);
      throw e;
    }
  }, [listDatasets, setOutputMessage, formatError, normalizeRowCount]);

  const onEngineDiagnostics = useCallback(async () => {
    try {
      if (typeof getEngineDiagnostics === 'function') {
        const d = await getEngineDiagnostics();
        setOutputMessage(`Engine diagnostics:\n${JSON.stringify(d, null, 2)}`);
        return d;
      }
      setOutputMessage('Diagnostics not available from provider. Expose getEngineDiagnostics() to use this.');
      return null;
    } catch (e) {
      setOutputMessage(`Diagnostics failed: ${formatError(e)}`, true);
      throw e;
    }
  }, [getEngineDiagnostics, setOutputMessage, formatError]);

  const onReinitDb = useCallback(async (opts = { mode: 'file', dropOldFile: true, reAdd: false }) => {
    try {
      setOutputMessage('Reinitializing DuckDB…');
      const info = await reinitEngineDb(opts);
      setOutputMessage(`Reinit complete:\n${JSON.stringify(info, null, 2)}`);
      return info;
    } catch (e) {
      setOutputMessage(`Reinit failed: ${formatError(e)}`, true);
      throw e;
    }
  }, [reinitEngineDb, setOutputMessage, formatError]);

  const onCopyDebugBundle = useCallback(async () => {
    try {
      const { text } = await buildDebugBundle({
        selectedDatasetId,
        queryText,
        sampleRows: lastQueryRows,
        totalRowsLastRun: Array.isArray(lastQueryRows) ? lastQueryRows.length : 0,
        ui: {
          lastOutput: { text: output?.text || '', isError: !!output?.isError },
          resolvedTable: resolvedTable || null,
        },
      });
      const ok = await copyToClipboard(text);
      setOutputMessage(ok ? 'Debug bundle copied to clipboard.' : 'Failed to copy debug bundle.', !ok);
      return ok;
    } catch (e) {
      setOutputMessage(`Failed to build/copy debug bundle: ${formatError(e)}`, true);
      throw e;
    }
  }, [buildDebugBundle, selectedDatasetId, queryText, lastQueryRows, output, resolvedTable, copyToClipboard, setOutputMessage, formatError]);

  return {
    // provider passthrough
    engineReady,
    engineError,
    initEngine,
    statusByDatasetId,
    getEngineInfo,
    flushFiles,
    subscribeDataset,
    // local ui state
    selectedDatasetId, setSelectedDatasetId,
    loadedDataset, 
    intervalMs, setIntervalMs,
    queryText, setQueryText,
    lastQueryRows,
    output,
    resolvedTable,
    logInfo,
    logError,

    // history
    history,
    clearHistory,
    getHistoryText,

    // actions
    loadAndRegister,     // (id|dataset?, { refreshOnAdd })
    registerDataset,     // (dataset, { refreshOnAdd })
    onManualRefresh,     // (id|dataset?)
    onSetInterval,       // (id|dataset?)
    onRunQuery,          // (id|dataset?, sql?)
    onListDatasets,
    onEngineDiagnostics,
    onReinitDb,
    onCopyDebugBundle,
  };
}

export default useDataset;
